import React, { useState, useEffect, createContext } from 'react';
import axios from 'axios';

interface AlgoliaContextType {
    domain: string;
    isFullUrl: (param: string) => boolean,
    categories: SubCategory[];
    windowHrefTids: string[];
    getCurrentTidParams: () => string[];
    isMobile: boolean;
};

interface SubCategory {
    category_id: string;
    category_name: string;
    sub_categories: SubSubCategory[];
};

interface SubSubCategory {
    category_id: string;
    category_name: string;
    master_id: string; 
};

declare global {
    interface Window {
      tids: string[];
    }
};  

export const AlgoliaContext = createContext<AlgoliaContextType>({} as AlgoliaContextType);

export const AlgoliaProvider = (props: { children: React.ReactNode }) => {

    const domain = 'https://www.agemd.com';

    const [styles, setStyles] = useState<any>({});

    useEffect(() => {
        axios.get(domain + "/api/widget/html/get/get-styles").then((response) => {
            setStyles(response.data);
        }).catch((error) => {
            console.log(error);
        });
    }, []);

    function isFullUrl(input: string): boolean {
        // Check if the input starts with a protocol, e.g., "http://", "https://", "ftp://", etc.
        if (input.match(/^https?:\/\/.*/)) {
            return true; // Full URL
        }

        // Check if the input starts with "www." and contains a valid domain
        if (input.match(/^www\.[a-zA-Z0-9\-]+(\.[a-zA-Z]{2,})+$/)) {
            return true; // Full URL (assuming it's a valid domain)
        }

        return false; // Route or invalid input
    };

    const [categories, setCategories] = useState<SubCategory[]>([]);

    const fetchCategories = async () => {

        const res = await axios.get(`${domain}/api/widget/html/get/fetch-categories`);

        if (res.data.categories && res.data.categories.length > 0) {
            setCategories(res.data.categories.sort((a:SubCategory, b:SubCategory) => {
                const categoryA = a.category_name.toLowerCase();
                const categoryB = b.category_name.toLowerCase();
              
                if (categoryA < categoryB) {
                  return -1;
                }
                if (categoryA > categoryB) {
                  return 1;
                }
                return 0;
              }) 
            );
        };

    };
    
    useEffect(() => {
        fetchCategories();
    }, []);

    const [windowHrefTids, setWindowHrefTids] = useState<string[]>([]);

    const getCurrentTidParams:() => string[] = () => {

        const urlParams = new URLSearchParams(window.location.search);
        const tid = urlParams.get('tid');

        const windowTids = window.tids;

        let tids:string[] = [];
        
        if (!tid && (!windowTids || windowTids.length === 0)) {
            return [];
        };

        if (tid) {

            const splitUrlTids:string[] = tid.includes(',') ? tid.split(',').map((t) => t.trim()) : [tid];
            tids = splitUrlTids;

        };

        if (windowTids && windowTids.length > 0) {
            tids = [...tids, ...windowTids];
        };
        
        return tids;
    };

    useEffect(() => {
        setWindowHrefTids(getCurrentTidParams());
    }, []);

    const [isMobile, setIsMobile] = useState<boolean>(false);

    const handleResize = (): void => {
      const screenWidth = window.innerWidth;
      setIsMobile(screenWidth <= 768); // Adjust the breakpoint as per your needs
    };
  
    useEffect(() => {
      // Add event listener for window resize
      window.addEventListener('resize', handleResize);
  
      // Call the handleResize function once when the component mounts
      handleResize();
  
      // Clean up the event listener when the component unmounts
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);
           

    return (

        <AlgoliaContext.Provider
            value={{
                domain,
                isFullUrl,
                categories,
                getCurrentTidParams,
                isMobile,
                windowHrefTids
            }}
        >
            {props.children}
        </AlgoliaContext.Provider>

    )

}

export default AlgoliaContext
